/** Pure helpers ported verbatim from the reference component (fmt, fmtP, dur, dayLabel, tog, chart, TRADES). */
import { INSTRUMENTS, canonicalTrades } from '@pointfive/contracts';
import type { ModelState } from './state';

export const fmt = (n: number): string =>
  (n < 0 ? '−$' : '+$') +
  Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const fmtP = (n: number): string =>
  '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const dur = (s: number): string =>
  s >= 3600
    ? Math.floor(s / 3600) + 'h ' + Math.round((s % 3600) / 60) + 'm'
    : Math.floor(s / 60) + 'm ' + (s % 60) + 's';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "24 Jul, 08:40" for a day offset from the reference present (2026-07-24 08:40 UTC). */
export function dayLabel(off: number): string {
  const base = Date.UTC(2026, 6, 24, 8, 40);
  const d = new Date(base - off * 86400000);
  return (
    d.getUTCDate() +
    ' ' +
    MONTHS[d.getUTCMonth()] +
    ', ' +
    String(d.getUTCHours()).padStart(2, '0') +
    ':' +
    String(d.getUTCMinutes()).padStart(2, '0')
  );
}
export interface Tog {
  trackBg: string;
  knob: string;
  knobBg: string;
}
export function tog(on: boolean, ac?: string): Tog {
  const a = ac || 'var(--pos)';
  return {
    trackBg: on ? a : 'var(--l7)',
    knob: on ? 'translateX(14px)' : 'translateX(0px)',
    knobBg: on ? 'var(--surface)' : 'var(--muted2)',
  };
}
/** Linear-congruential generator the reference uses for all seeded series. */
export function lcg(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

export interface Candle {
  left: string;
  w: string;
  wickLeft: string;
  wickTop: string;
  wickH: string;
  bodyTop: string;
  bodyH: string;
  col: string;
  volH: string;
  volCol: string;
}
export interface ChartModel {
  candles: Candle[];
  emaPts: string;
  ticks: { top: string; label: string }[];
  times: { left: string; label: string }[];
  lastPrice: string;
  lastTop: string;
  lastBg: string;
  o: string;
  h: string;
  l: string;
  c: string;
  chg: string;
  chgPct: string;
  chgCol: string;
  dayLo: string;
  dayHi: string;
  rangePos: string;
  bid: string;
  ask: string;
  spread: string;
  scaleLo: number;
  scaleHi: number;
  barCount: string;
}
/** The desk's instruments, at the prices the demo quotes them (`packages/sim` exec.quotes), with the decimals each
 *  is conventionally shown to. The header, the ticket and the chart all price off this one table, so a symbol change
 *  moves the three together. */
/** The instrument table now lives in contracts, because the seed writes candles against it and the datafeed serves
 *  them back — three readers, one table, or the price on the chart stops matching the price on the ticket. */
const INSTRUMENT = INSTRUMENTS;

/** The quoted scale of an instrument: its base price and the decimals it is shown to.
 *
 *  Exported so the order ticket sizes its default stop and target the same way the chart sizes its candles — one
 *  table, so the two cannot disagree about what a sensible distance is on this instrument. */
export function instrumentScale(sym: string): { base: number; decimals: number } {
  return INSTRUMENT[sym || 'XAUUSD'] ?? INSTRUMENT['XAUUSD']!;
}

/** Seeded candle series per instrument and timeframe — the reference's `chart()`, widened from gold alone.
 *
 *  The reference hardcoded 4036 and two decimals, because its desk only ever showed XAUUSD. Quoting EURUSD at
 *  "4036.12" would be worse than showing nothing: it reads like a price. */
export function chart(S: Pick<ModelState, 'tf' | 'deskSym'>): ChartModel {
  const stepMin =
    ({ '1m': 1, '5m': 5, '15m': 15, '1h': 60, '4h': 240, '1d': 1440 } as Record<string, number>)[S.tf] || 60;
  const N = 60;
  const inst = INSTRUMENT[S.deskSym || 'XAUUSD'] ?? INSTRUMENT['XAUUSD']!;
  // Seeded by instrument as well as timeframe, so each one has its own shape rather than gold's rescaled. The
  // offset is zero for XAUUSD on purpose: it is the instrument the whole reference was drawn against, and its
  // series has to stay identical to the digit or every figure on the desk moves.
  const symbol = S.deskSym || 'XAUUSD';
  const symSeed =
    symbol === 'XAUUSD' ? 0 : [...symbol].reduce((a, ch) => (a * 31 + ch.charCodeAt(0)) % 997, 0);
  let seed = 7 + stepMin + symSeed,
    p = inst.base;
  const rnd = () => (seed = (seed * 9301 + 49297) % 233280) / 233280;
  // Proportional to the instrument, not absolute: 1.6 points is a gold bar and a EURUSD apocalypse. Expressed as a
  // ratio against gold's own base so XAUUSD's series is unchanged to the digit.
  const vola = Math.sqrt(stepMin) * 1.6 * (inst.base / 4036);
  const raw: { o: number; c: number; hi: number; lo: number; v: number }[] = [];
  for (let i = 0; i < N; i++) {
    const o = p,
      c = p + (rnd() - 0.47) * vola;
    const hi = Math.max(o, c) + rnd() * vola * 0.45,
      lo = Math.min(o, c) - rnd() * vola * 0.45;
    raw.push({ o, c, hi, lo, v: 0.35 + Math.abs(c - o) / vola + rnd() * 0.5 });
    p = c;
  }
  const min = Math.min(...raw.map((r) => r.lo)),
    max = Math.max(...raw.map((r) => r.hi));
  const pad = (max - min) * 0.08,
    lo = min - pad,
    hi = max + pad;
  const y = (v: number) => ((hi - v) / (hi - lo)) * 100;
  const w = 100 / N,
    maxV = Math.max(...raw.map((r) => r.v));
  // Each instrument to the decimals it is quoted in. Gold to two, EURUSD to five — a price shown to the wrong
  // precision is a different price.
  const f = (v: number) => v.toFixed(inst.decimals);
  const candles: Candle[] = raw.map((r, i) => {
    const up = r.c >= r.o;
    return {
      left: (i * w).toFixed(3) + '%',
      w: (w * 0.6).toFixed(3) + '%',
      wickLeft: ((i + 0.3) * w).toFixed(3) + '%',
      wickTop: y(r.hi).toFixed(2) + '%',
      wickH: Math.max(0.4, y(r.lo) - y(r.hi)).toFixed(2) + '%',
      bodyTop: y(Math.max(r.o, r.c)).toFixed(2) + '%',
      bodyH: Math.max(0.6, Math.abs(y(r.o) - y(r.c))).toFixed(2) + '%',
      col: up ? 'var(--pos)' : 'var(--neg)',
      volH: ((r.v / maxV) * 100).toFixed(1) + '%',
      volCol: up
        ? 'color-mix(in srgb, var(--pos) 42%, transparent)'
        : 'color-mix(in srgb, var(--neg) 38%, transparent)',
    };
  });
  let e = raw[0]!.c;
  const k = 2 / 21;
  const emaPts = raw
    .map((r, i) => {
      e = r.c * k + e * (1 - k);
      return ((i + 0.3) * w).toFixed(2) + ',' + y(e).toFixed(2);
    })
    .join(' ');
  const ticks: { top: string; label: string }[] = [];
  for (let i = 0; i <= 4; i++) ticks.push({ top: i * 25 + '%', label: f(hi - ((hi - lo) * i) / 4) });
  const times: { left: string; label: string }[] = [];
  const endMin = 14 * 60;
  for (let i = 0; i < 6; i++) {
    const idx = Math.round((i * (N - 1)) / 5);
    const m = (((endMin - (N - 1 - idx) * stepMin) % 1440) + 1440) % 1440;
    times.push({
      left: ((idx + 0.3) * w).toFixed(2) + '%',
      label: String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0'),
    });
  }
  const last = raw[N - 1]!,
    first = raw[0]!,
    chg = last.c - first.o;
  return {
    candles,
    emaPts,
    ticks,
    times,
    lastPrice: f(last.c),
    lastTop: y(last.c).toFixed(2) + '%',
    lastBg: last.c >= last.o ? 'var(--pos)' : 'var(--neg)',
    o: f(last.o),
    h: f(last.hi),
    l: f(last.lo),
    c: f(last.c),
    chg: (chg >= 0 ? '+' : '−') + Math.abs(chg).toFixed(inst.decimals),
    chgPct: (chg >= 0 ? '+' : '−') + Math.abs((chg / first.o) * 100).toFixed(2) + '%',
    chgCol: chg >= 0 ? 'var(--pos)' : 'var(--neg)',
    dayLo: f(min),
    dayHi: f(max),
    rangePos: (((last.c - min) / (max - min)) * 100).toFixed(1) + '%',
    // Half-spread in the instrument's own units. The reference's flat ±0.1 is 20 gold points; on EURUSD it would be
    // a ten-cent spread, which is not a quote, it is a typo.
    bid: f(last.c - inst.halfSpread),
    ask: f(last.c + inst.halfSpread),
    spread: inst.spread,
    scaleLo: lo,
    scaleHi: hi,
    barCount: N + ' bars · ' + S.tf + ' · MetaAPI',
  };
}

/** One canonical trade row: [id, dayOff, acct, sym, side, lots, entry, exit, pnl, riskUnit, strat, tagged, time]. */
export type { TradeTuple } from '@pointfive/contracts';
/** Canonical trade list — the reviewed sessions injected as EX_TRADES-shaped rows ahead of EX_TRADES (reference
 *  TRADES()). The derivation lives in `@pointfive/contracts` because the Phase-2 seed writes the same rows into MySQL;
 *  one definition means the ledger on screen and the ledger in the database cannot drift apart. */
export const trades = canonicalTrades;

/** Where a trailing stop currently sits (FLOW-LOCKOUT's `trail` rule).
 *
 *  A trailing stop keeps the distance it was opened with and follows the price in the trader's favour only. So the
 *  level is the *better* of where the stop was placed and where the trailing distance puts it now — never worse,
 *  which is the whole point: a stop that could move against you is not a stop.
 *
 *  **What this cannot do, and does not pretend to:** a real trailing stop ratchets off the high-water mark of the
 *  price since entry, so it stays put when the price retraces. There is no tick history here — the quote is a
 *  deterministic function of state, not a feed — so this returns the level the *current* price implies. On a
 *  retracement the honest answer and the ratcheted one differ, and closing that gap needs a stored high-water mark
 *  fed by real ticks (CMP-MKT).
 *
 *  Returns the original stop unchanged when the trade is not yet in profit: the trail has nothing to follow.
 */
export function trailingStop(input: {
  entry: number;
  stop: number;
  current: number;
  isLong: boolean;
}): number {
  const { entry, stop, current, isLong } = input;
  if (!isFinite(entry) || !isFinite(stop) || !isFinite(current)) return stop;
  const distance = Math.abs(entry - stop);
  if (!(distance > 0)) return stop;
  return isLong ? Math.max(stop, current - distance) : Math.min(stop, current + distance);
}
